<template>
  <div class="mx-auto p-4 bg-warm-gray-900 min-h-screen">
    <div class="container mx-auto">
      <h1 class="text-2xl font-bold mb-6 text-gray-100">Users-Reviewers Explorer</h1>
      
      <!-- Tabs -->
      <div class="mb-6 flex space-x-2 border-b border-gray-700">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="switchTab(tab.id)"
          :class="[
            'px-4 py-2 font-medium transition-colors',
            activeTab === tab.id
              ? 'text-yellow-400 border-b-2 border-yellow-400'
              : 'text-gray-400 hover:text-gray-300'
          ]"
        >
          {{ tab.name }}
        </button>
      </div>
      
      <!-- Filters -->
      <div v-if="!loading" class="mb-6 p-4 bg-gray-800 rounded">
        <div class="space-y-3">
          <div class="text-gray-200 font-semibold mb-3">Filters:</div>
          
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label class="flex items-center text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  v-model="filters.expired_status"
                  class="mr-2 w-4 h-4 text-yellow-500 bg-gray-700 border-gray-600 rounded focus:ring-yellow-500 cursor-pointer"
                />
                <span>Expired (Status)</span>
              </label>
              <p class="text-xs text-gray-500 italic ml-6 mt-1">Reviewer status is set to "expired"</p>
            </div>
            
            <div>
              <label class="flex items-center text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  v-model="filters.expired_date"
                  class="mr-2 w-4 h-4 text-yellow-500 bg-gray-700 border-gray-600 rounded focus:ring-yellow-500 cursor-pointer"
                />
                <span>Expired (Date) - Should Be Expired</span>
              </label>
              <p class="text-xs text-gray-500 italic ml-6 mt-1">Status is "active" but expiration date has passed</p>
            </div>
            
            <div>
              <label class="flex items-center text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  v-model="filters.active"
                  class="mr-2 w-4 h-4 text-yellow-500 bg-gray-700 border-gray-600 rounded focus:ring-yellow-500 cursor-pointer"
                />
                <span>Active</span>
              </label>
              <p class="text-xs text-gray-500 italic ml-6 mt-1">Reviewer is active and expiration date is in the future</p>
            </div>
            
            <div>
              <label class="flex items-center text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  v-model="filters.legacy_incomplete"
                  class="mr-2 w-4 h-4 text-yellow-500 bg-gray-700 border-gray-600 rounded focus:ring-yellow-500 cursor-pointer"
                />
                <span>Legacy Expired (Incomplete Data)</span>
              </label>
              <p class="text-xs text-gray-500 italic ml-6 mt-1">Status is "inactive" with data but missing expiration_date</p>
            </div>
            
            <div>
              <label class="flex items-center text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  v-model="filters.legacy_complete"
                  class="mr-2 w-4 h-4 text-yellow-500 bg-gray-700 border-gray-600 rounded focus:ring-yellow-500 cursor-pointer"
                />
                <span>Legacy Expired (Complete Data)</span>
              </label>
              <p class="text-xs text-gray-500 italic ml-6 mt-1">Status is "inactive" with complete data including expiration_date</p>
            </div>
            
            <div>
              <label class="flex items-center text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  v-model="filters.confirmation_pending"
                  class="mr-2 w-4 h-4 text-yellow-500 bg-gray-700 border-gray-600 rounded focus:ring-yellow-500 cursor-pointer"
                />
                <span>Confirmation Pending</span>
              </label>
              <p class="text-xs text-gray-500 italic ml-6 mt-1">Status is "paid" - user paid but admin hasn't confirmed yet</p>
            </div>
            
            <div>
              <label class="flex items-center text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  v-model="filters.waiting_for_activation"
                  class="mr-2 w-4 h-4 text-yellow-500 bg-gray-700 border-gray-600 rounded focus:ring-yellow-500 cursor-pointer"
                />
                <span>Waiting for Activation</span>
              </label>
              <p class="text-xs text-gray-500 italic ml-6 mt-1">Status is "ready" - payment confirmed, waiting for activation</p>
            </div>
            
            <div>
              <label class="flex items-center text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  v-model="filters.never_bought"
                  class="mr-2 w-4 h-4 text-yellow-500 bg-gray-700 border-gray-600 rounded focus:ring-yellow-500 cursor-pointer"
                />
                <span>Never Bought</span>
              </label>
              <p class="text-xs text-gray-500 italic ml-6 mt-1">Status is "inactive" and no reviewer data exists</p>
            </div>
          </div>
        </div>
      </div>
      
      <div v-if="loading" class="text-gray-300">Loading...</div>
      
      <div v-else-if="filteredUsers.length > 0">
        <p class="text-gray-300 mb-4">Total: {{ filteredUsers.length.toLocaleString() }} users</p>
        <ul class="space-y-4">
          <li
            v-for="user in filteredUsers"
            :key="user.id"
            class="bg-gray-800 p-4 rounded"
          >
            <div class="text-gray-200 mb-4">
              <strong>Email:</strong> {{ user.data.email || 'N/A' }}
              <span class="text-gray-400 ml-4">
                <strong>Created:</strong> {{ formatDate(user.data.date_created) }}
              </span>
            </div>
            
            <!-- Category Badge -->
            <div class="mb-4">
              <span
                :class="getCategoryColor(user)"
                class="px-3 py-1 rounded text-sm font-semibold"
              >
                {{ getCategoryLabel(user) }}
              </span>
            </div>
            
            <!-- Reviewer Details -->
            <div v-if="getReviewerData(user)" class="text-gray-300 space-y-2">
              <div class="font-semibold text-gray-200 mb-2">{{ getReviewerName() }}</div>
              <div class="ml-4 space-y-2">
                <div><strong>Status:</strong> {{ getReviewerStatus(user) || 'inactive' }}</div>
                <div v-if="isShouldBeExpired(getReviewerStatus(user), getReviewerData(user)?.expiration_date)" class="text-red-500 font-semibold">
                  • ACTIVE BUT SHOULD BE EXPIRED
                </div>
                <div v-if="getReviewerData(user)?.purchase_date">
                  <strong>Purchase Date:</strong> {{ formatDate(getReviewerData(user).purchase_date) }}
                </div>
                <div v-if="getReviewerData(user)?.activation_date">
                  <strong>Activation Date:</strong> {{ formatFirestoreTimestamp(getReviewerData(user).activation_date) }}
                </div>
                <div v-if="getReviewerData(user)?.expiration_date">
                  <strong>Expiration Date:</strong> {{ formatFirestoreTimestamp(getReviewerData(user).expiration_date) }}
                </div>
                <div>
                  <strong>Data:</strong>
                  <pre class="text-xs mt-1 bg-gray-900 p-2 rounded overflow-auto">{{ getReviewerData(user) || null }}</pre>
                </div>
              </div>
            </div>
            
            <!-- Never Bought Message -->
            <div v-else class="text-gray-400 italic">
              User has never bought {{ getReviewerName() }}
            </div>
          </li>
        </ul>
      </div>
      
      <div v-else class="text-gray-300">No users found</div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { db } from '@/firebase/config'
import { collection, query, getDocs, where } from 'firebase/firestore'

const loading = ref(false)
const users = ref([])
const activeTab = ref('caap')

const tabs = [
  { id: 'caap', name: 'CAAP' },
  { id: 'caap_plus', name: 'CAAP Plus' },
  { id: 'mtp', name: 'MTP' }
]

const filters = ref({
  expired_status: false,
  expired_date: false,
  active: false,
  legacy_incomplete: false,
  legacy_complete: false,
  confirmation_pending: false,
  waiting_for_activation: false,
  never_bought: false
})

// Helper: Check if user bought a reviewer
const hasBoughtReviewer = (user, reviewerType) => {
  const dataKey = `${reviewerType}_data`
  return user.data[dataKey] !== null && user.data[dataKey] !== undefined
}

// Helper: Check if reviewer data is complete (has expiration_date)
const hasCompleteData = (reviewerData) => {
  return reviewerData && reviewerData.expiration_date
}

// Helper: Check if expiration date has passed
const isExpirationDatePassed = (expirationDate) => {
  if (!expirationDate || !expirationDate.seconds) return false
  const milliseconds = expirationDate.seconds * 1000 + Math.floor(expirationDate.nanoseconds / 1000000)
  return new Date(milliseconds) < new Date()
}

// Categorize a user for a specific reviewer
const categorizeUserReviewer = (user, reviewerType) => {
  const status = user.data.reviewers?.[reviewerType] || 'inactive'
  const dataKey = `${reviewerType}_data`
  const reviewerData = user.data[dataKey]
  
  // Never bought
  if (!hasBoughtReviewer(user, reviewerType)) {
    return 'never_bought'
  }
  
  // Expired by status
  if (status === 'expired') {
    return 'expired_status'
  }
  
  // Confirmation pending (paid)
  if (status === 'paid') {
    return 'confirmation_pending'
  }
  
  // Waiting for activation (ready)
  if (status === 'ready') {
    return 'waiting_for_activation'
  }
  
  // Active status
  if (status === 'active') {
    if (hasCompleteData(reviewerData)) {
      if (isExpirationDatePassed(reviewerData.expiration_date)) {
        return 'expired_date' // Should be expired
      } else {
        return 'active'
      }
    } else {
      return 'legacy_incomplete' // Active but missing expiration_date
    }
  }
  
  // Inactive status but has data (legacy)
  if (status === 'inactive' || !status || status === '') {
    if (hasCompleteData(reviewerData)) {
      return 'legacy_complete'
    } else {
      return 'legacy_incomplete'
    }
  }
  
  return 'unknown'
}

// Format date_created timestamp string to readable date
const formatDate = (dateCreated) => {
  if (!dateCreated) return 'N/A'
  
  const timestamp = typeof dateCreated === 'string' ? parseInt(dateCreated) : dateCreated
  
  const date = timestamp.toString().length === 13 
    ? new Date(timestamp) 
    : new Date(timestamp * 1000)
  
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

// Format Firestore Timestamp object to readable date
const formatFirestoreTimestamp = (timestamp) => {
  if (!timestamp) return 'N/A'
  
  if (timestamp.seconds !== undefined) {
    const milliseconds = timestamp.seconds * 1000 + Math.floor(timestamp.nanoseconds / 1000000)
    const date = new Date(milliseconds)
    
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }
  
  return 'N/A'
}

// Check if a reviewer should be expired (status is active but expiration_date has passed)
const isShouldBeExpired = (status, expirationDate) => {
  if (status !== 'active' || !expirationDate) {
    return false
  }
  
  if (expirationDate.seconds !== undefined) {
    const milliseconds = expirationDate.seconds * 1000 + Math.floor(expirationDate.nanoseconds / 1000000)
    const expiration = new Date(milliseconds)
    const now = new Date()
    
    return expiration < now
  }
  
  return false
}

// Get reviewer name for current tab
const getReviewerName = () => {
  const tab = tabs.find(t => t.id === activeTab.value)
  return tab ? tab.name : activeTab.value.toUpperCase()
}

// Get reviewer status for current tab
const getReviewerStatus = (user) => {
  return user.data.reviewers?.[activeTab.value] || 'inactive'
}

// Get reviewer data for current tab
const getReviewerData = (user) => {
  const dataKey = `${activeTab.value}_data`
  return user.data[dataKey]
}

// Get category label
const getCategoryLabel = (user) => {
  const category = categorizeUserReviewer(user, activeTab.value)
  const labels = {
    never_bought: 'Never Bought',
    expired_status: 'Expired (Status)',
    expired_date: 'Expired (Date) - Should Be Expired',
    active: 'Active',
    legacy_incomplete: 'Legacy Expired (Incomplete Data)',
    legacy_complete: 'Legacy Expired (Complete Data)',
    confirmation_pending: 'Confirmation Pending',
    waiting_for_activation: 'Waiting for Activation',
    unknown: 'Unknown'
  }
  return labels[category] || 'Unknown'
}

// Get category color
const getCategoryColor = (user) => {
  const category = categorizeUserReviewer(user, activeTab.value)
  const colors = {
    never_bought: 'bg-gray-600 text-gray-200',
    expired_status: 'bg-yellow-900 text-yellow-200',
    expired_date: 'bg-red-900 text-red-200',
    active: 'bg-green-900 text-green-200',
    legacy_incomplete: 'bg-purple-900 text-purple-200',
    legacy_complete: 'bg-blue-900 text-blue-200',
    confirmation_pending: 'bg-orange-900 text-orange-200',
    waiting_for_activation: 'bg-cyan-900 text-cyan-200',
    unknown: 'bg-gray-700 text-gray-300'
  }
  return colors[category] || colors.unknown
}

// Filter users based on selected filters
const filteredUsers = computed(() => {
  // If no filters selected, show all users
  const hasAnyFilter = Object.values(filters.value).some(v => v)
  if (!hasAnyFilter) {
    return users.value
  }
  
  return users.value.filter(user => {
    const category = categorizeUserReviewer(user, activeTab.value)
    
    if (category === 'expired_status' && filters.value.expired_status) return true
    if (category === 'expired_date' && filters.value.expired_date) return true
    if (category === 'active' && filters.value.active) return true
    if (category === 'legacy_incomplete' && filters.value.legacy_incomplete) return true
    if (category === 'legacy_complete' && filters.value.legacy_complete) return true
    if (category === 'confirmation_pending' && filters.value.confirmation_pending) return true
    if (category === 'waiting_for_activation' && filters.value.waiting_for_activation) return true
    if (category === 'never_bought' && filters.value.never_bought) return true
    
    return false
  })
})

// Switch tab and fetch users
const switchTab = async (tabId) => {
  activeTab.value = tabId
  await fetchUsersForReviewer(tabId)
}

// Fetch users for a specific reviewer
const fetchUsersForReviewer = async (reviewerType) => {
  loading.value = true
  try {
    const userMap = new Map()
    
    // Query all possible statuses for this reviewer
    const statuses = ['paid', 'ready', 'active', 'expired', 'inactive']
    const queries = statuses.map(status =>
      getDocs(query(
        collection(db, 'users'),
        where(`reviewers.${reviewerType}`, '==', status)
      ))
    )
    
    const results = await Promise.all(queries)
    
    // Combine results and deduplicate by user ID
    results.forEach(querySnapshot => {
      querySnapshot.forEach(doc => {
        const data = doc.data()
        const userId = doc.id
        
        if (!userMap.has(userId)) {
          userMap.set(userId, {
            id: userId,
            data: data
          })
        }
      })
    })
    
    // Also need to fetch users with null/undefined status but have _data
    // Query for users where the reviewer field doesn't exist or is null
    // This is tricky in Firestore - we'll fetch users with inactive status
    // and also check for users with _data but no status
    
    // Filter to include users who have status OR have _data
    const allUsers = Array.from(userMap.values())
    const dataKey = `${reviewerType}_data`
    
    users.value = allUsers
      .filter(user => {
        const hasStatus = user.data.reviewers?.[reviewerType]
        const hasData = hasBoughtReviewer(user, reviewerType)
        // Include if they have status OR have data
        return hasStatus || hasData
      })
      .sort((a, b) => {
        const emailA = a.data.email || ''
        const emailB = b.data.email || ''
        return emailA.localeCompare(emailB)
      })
    
  } catch (error) {
    console.error('Error fetching users:', error)
    users.value = []
  } finally {
    loading.value = false
  }
}

// Watch for tab changes
watch(activeTab, () => {
  fetchUsersForReviewer(activeTab.value)
})

// Initial load
onMounted(() => {
  fetchUsersForReviewer(activeTab.value)
})
</script>

<style scoped>
pre {
  max-height: 200px;
}
</style>
